import { LiveblocksCommentMark, TaskItemNode } from '@seen/core/tiptap/schema'
import { linkVariants } from '@seen/ui'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import TaskList from '@tiptap/extension-task-list'
import Typography from '@tiptap/extension-typography'
import type { Extensions } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { AddTimelineNoteButtonNode } from '#src/components/AddTimelineNoteButtonNode.tsx'
import { AIProposalsExtension } from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import { CarePlanQuestionnaireNode } from '#src/components/rich-text-editor/CarePlanQuestionnaireNode/CarePlanQuestionnaireNode.tsx'
import { ChoiceNode } from '#src/components/rich-text-editor/choice/ChoiceNode.tsx'
import {
  ColumnBlockNode,
  ColumnNode,
} from '#src/components/rich-text-editor/column/index.ts'
import { ConditionalDisplayExtension } from '#src/components/rich-text-editor/conditional-display/ConditionalDisplayExtension.ts'
import { ConditionalSectionNode } from '#src/components/rich-text-editor/conditional-display/ConditionalSectionNode.ts'
import { ConditionalSpanNode } from '#src/components/rich-text-editor/conditional-display/ConditionalSpanNode.ts'
import { NodeConfigExtension } from '#src/components/rich-text-editor/conditional-display/NodeConfigExtension.ts'
import { ConditionsAddressedBlockNode } from '#src/components/rich-text-editor/ConditionsAddressedBlockNode/ConditionsAddressedBlockNode.tsx'
import { CreateTaskShortcutExtension } from '#src/components/rich-text-editor/CreateTaskShortcutExtension.ts'
import { DocumentReferenceNode } from '#src/components/rich-text-editor/document-reference/DocumentReferenceNode.tsx'
import { EncounterMedicationsBlockNode } from '#src/components/rich-text-editor/EncounterMedicationsBlockNode/EncounterMedicationsBlockNode.tsx'
import { EncounterNode } from '#src/components/rich-text-editor/EncounterNode/EncounterNode.tsx'
import { InsertAllergiesButtonNode } from '#src/components/rich-text-editor/InsertAllergiesButtonNode.tsx'
import { InsertConditionsButtonNode } from '#src/components/rich-text-editor/InsertConditionsButtonNode.tsx'
import { InsertCurrentConditionsButtonNode } from '#src/components/rich-text-editor/InsertCurrentConditionsButtonNode.tsx'
import { InsertCurrentConditionsWithNotesButtonNode } from '#src/components/rich-text-editor/InsertCurrentConditionsWithNotesButtonNode.tsx'
import { InsertMedicationsButtonNode } from '#src/components/rich-text-editor/InsertMedicationsButtonNode.tsx'
import { MedicationRefillBlockNode } from '#src/components/rich-text-editor/MedicationRefillBlockNode/MedicationRefillBlockNode.tsx'
import { MentionNode } from '#src/components/rich-text-editor/mention/MentionNode.tsx'
import { ObservationNode } from '#src/components/rich-text-editor/ObservationNode/ObservationNode.tsx'
import { ObservationPanelNode } from '#src/components/rich-text-editor/ObservationNode/panel/ObservationPanelNode.ts'
import { RegistryVariableNode } from '#src/components/rich-text-editor/registry-variable/RegistryVariableNode.ts'
import { RequiredAnswersExtension } from '#src/components/rich-text-editor/required/RequiredAnswersExtension.ts'
import { SignatureButtonNode } from '#src/components/rich-text-editor/SignatureButtonNode.tsx'
import { TemplateVariableNode } from '#src/components/rich-text-editor/template-variable/TemplateVariableNode.tsx'
import { ValidatedToolNode } from '#src/components/rich-text-editor/ValidatedToolNode/ValidatedToolNode.tsx'

/* Styles StarterKit's bundled Link. Exported because the editor path
 * (useTiptapExtensions) re-.configure()s StarterKit to toggle its undo/redo
 * history per editor, and must respread these options so the styling survives. */
export const STARTER_KIT_OPTIONS = {
  link: { HTMLAttributes: { class: linkVariants() } },
}

/* Every node type the editor can produce in any non-markdown, non-simpleText
 * config. Used by useTiptapExtensions (composed with EDITOR_HELPERS +
 * context-aware .configure() per call site), RichText.tsx (read-only
 * renderer), and noteContent.ts (HTML extraction via generateHTML).
 *
 * Returned by a function rather than held as a top-level const because several
 * of these node modules transitively import RichTextEditor → useTiptapExtensions
 * → this file, forming an import cycle. A const array literal evaluates the
 * imported bindings eagerly during module init and hits TDZ; a function body
 * evaluates them lazily at call time, after every module in the cycle has
 * finished its top-level work.
 *
 * Order mirrors useTiptapExtensions's compact() block so editor and renderer
 * agree on mark/node precedence. */
export function getRenderExtensions(): Extensions {
  return [
    StarterKit.configure(STARTER_KIT_OPTIONS),
    Typography,
    TaskList,
    TaskItemNode,
    Image,
    /* The mark @liveblocks/react-tiptap writes into stored note content.
     * generateHTML throws on any note carrying a comment thread without it, and
     * `contentToHTML` calls generateHTML with no try/catch. The collaborative
     * editors compose the vendor's own copy instead — see
     * useTiptapExtensions's liveblocksProvidesCommentMark. */
    LiveblocksCommentMark,
    DocumentReferenceNode,
    TemplateVariableNode,
    ChoiceNode,
    MentionNode,
    RegistryVariableNode,
    SignatureButtonNode,
    EncounterNode,
    EncounterMedicationsBlockNode,
    ValidatedToolNode,
    ObservationPanelNode,
    CarePlanQuestionnaireNode,
    AddTimelineNoteButtonNode,
    InsertAllergiesButtonNode,
    InsertConditionsButtonNode,
    InsertCurrentConditionsButtonNode,
    InsertCurrentConditionsWithNotesButtonNode,
    InsertMedicationsButtonNode,
    ObservationNode,
    ColumnBlockNode,
    ColumnNode,
    ConditionalSectionNode,
    ConditionalSpanNode,
    ConditionsAddressedBlockNode,
    MedicationRefillBlockNode,
    AIProposalsExtension,
  ]
}

/* Editor-only helpers — no node-type implications. Not needed for render or
 * extraction. Kept separate so static contexts (renderers, extractors,
 * backfill) don't pull in editor-only behavior. */
export function getEditorHelpers(): Extensions {
  return [
    Placeholder,
    CreateTaskShortcutExtension,
    ConditionalDisplayExtension,
    NodeConfigExtension,
    RequiredAnswersExtension,
  ]
}
